import { vec2 } from 'gl-matrix';
import { domainEventBus } from '~/domain/events';
import {
    WindowLevelDeltaEvent,
    WindowLevelSetEvent,
} from '~/domain/events/WindowingDomainEvents';
import { WindowLevel } from '~/domain/patient/windowLevel';
import {
    WindowingCursor,
    WindowingCursorOptions,
} from '../../data/common/customs/cursors/WindowingCursor';
import { Environment } from '../../data/Environment';
import { PlaneView } from '../../view/views/PlaneView';
import { MOUSE_BUTTON_RIGHT, MOUSE_BUTTONS_RIGHT_MASK } from '../MouseButton';
import { ModifierKeys } from '../ModifierKeys';
import { PointerLockManager } from '../PointerLockManager';
import { GlobalCoordinator } from './GlobalCoordinator';
import { PRIMARY_COLOR, SECONDARY_COLOR } from '~/theme/colors';
import { vec3ToHex } from '~/utils/colorUtils';

/**
 * Configuration for global right-click-drag windowing.
 */
const WINDOWING_CONFIG = {
    /** Which mouse axis adjusts window width. */
    widthAxis: 'x' as 'x' | 'y',
    /** Which mouse axis adjusts window center. */
    centerAxis: 'y' as 'x' | 'y',
    /** 'proportional': delta = movement * sensitivity.
     *  'direction': delta = +/-stepSize per frame (sign-only). */
    sensitivityMode: 'proportional' as 'proportional' | 'direction',
    /** Multiplier for proportional mode. */
    sensitivity: 1.5,
    /** Fixed step for direction mode (matches existing
     *  WindowingAction). */
    stepSize: 3,
    /** Pixels of movement before windowing activates
     *  (distinguishes stationary right-click from
     *  right-click-drag). */
    activationThreshold: 3,
    /** Mouse button for windowing. */
    mouseButton: MOUSE_BUTTON_RIGHT,
    /** Mouse button bitmask for windowing. */
    mouseButtonMask: MOUSE_BUTTONS_RIGHT_MASK,
};

/**
 * Owns all global right-click-drag windowing state and logic.
 *
 * Follows the same pattern as {@link GlobalPanCoordinator}:
 * consumed exclusively by {@link ToolManager} — every mouse/key
 * handler delegates to this coordinator first and skips further
 * dispatch when the coordinator returns `true` (event consumed).
 *
 * ## Pointer lock
 *
 * Pointer lock is requested on mousedown (the only reliable
 * user-gesture context). The browser grants it asynchronously.
 *
 * - If the user drags past the activation threshold the cursor
 *   overlay appears and windowing begins.
 * - If the click is stationary the lock is released on mouseup.
 * - When the browser exits pointer lock on its own (Esc key or
 *   tab switch) it fires `pointerlockchange`. We detect this and
 *   restore the saved WW/WC, since the browser swallows the Esc
 *   keydown event in this case.
 */
export class GlobalWindowingCoordinator extends GlobalCoordinator {
    /** The current {@link Environment} (for reading window level). */
    private environment: Environment | undefined;

    /** Whether the action button is currently held down. */
    private isActionButtonHeld = false;

    /** Whether windowing has activated (moved past threshold). */
    private _isActive = false;

    /** Coordinates where the action button was pressed
     *  (clientX/clientY for threshold check and cursor overlay). */
    private startCoordinates = vec2.create();

    /** Accumulated movement since action button press
     *  (for threshold check). */
    private accumulatedMovement = vec2.create();

    /** Accumulated windowing delta since activation
     *  (for cursor arrow direction). */
    private totalDelta = vec2.create();

    /** Snapshot of WW/WC before windowing (for Esc restore). */
    private savedWindowLevel: WindowLevel | undefined;

    /** Series UID that was snapshotted (for Esc restore). */
    private savedSeriesUid: string | undefined;

    /** Whether the current drag session targets the secondary image. */
    private isTargetingSecondary = false;

    /** Custom windowing cursor overlay. */
    private windowingCursor: WindowingCursor | undefined;

    /** Whether the cursor is shown pre-threshold (early feedback). */
    private isPreThresholdCursorShown = false;

    /** Manages the Pointer Lock API lifecycle. */
    private pointerLockManager = new PointerLockManager({
        onExternalExit: () => {
            // The browser exited pointer lock externally (Esc
            // or tab/window switch). Only restore WW/WC if
            // windowing was active (post-threshold); pre-
            // threshold ESC and lock denial have no deltas to
            // undo.
            if (this._isActive) {
                this.restoreWindowLevel();
            }
            this.deactivate();
        },
        onLockDenied: () => {
            // Pointer lock was denied (browser cooldown after
            // ESC). Windowing will continue with free cursor
            // movement; restore cursor visibility so the user
            // isn't left with an invisible cursor. Not optimal,
            // but it's still better UX to allow user to keep
            // adjusting windowing levels with a visible, moving
            // default cursor (and the stationary sun cursor)
            // than e.g. block windowing level adjustments entirely
            // until the browser cooldown is over.
            this.windowingCursor?.restoreCursorVisibility();
        },
    });

    // ───────── public API ─────────

    /** Whether windowing is currently active (past threshold). */
    public get isActive(): boolean {
        return this._isActive;
    }

    /**
     * Called by ToolManager.setActiveTool() to (re-)configure
     * the windowing coordinator.
     */
    public initialize(
        environment: Environment | undefined,
        views: PlaneView[] | undefined
    ): void {
        this.environment = environment;
        this.views = views;
        this.deactivate();
        // Belt-and-suspenders: release any orphaned pointer lock
        // or listener from a previous tool session.
        this.pointerLockManager.forceCleanUp();
    }

    /**
     * Windowing handling for mouse press.
     * @returns true if the event was consumed.
     */
    public onMousePress(
        button: number,
        coordinates: vec2,
        view: PlaneView,
        modifierKeys: ModifierKeys,
        isStrokeInProgress: () => boolean,
        isPanActive: boolean,
        toolUsesButtonDuringAction: (button: number) => boolean
    ): boolean {
        if (
            button === WINDOWING_CONFIG.mouseButton &&
            !isStrokeInProgress() &&
            !isPanActive &&
            !this._isActive
        ) {
            this.isActionButtonHeld = true;
            vec2.copy(this.startCoordinates, coordinates);
            vec2.zero(this.accumulatedMovement);

            // Decide target: secondary if Alt held AND a secondary
            // image is loaded; locked for the duration of the drag.
            this.isTargetingSecondary =
                modifierKeys.hasAlt &&
                this.environment?.secondaryImage?.parent?.seriesUid !==
                    undefined;

            // Snapshot current window level for Esc restore
            const seriesUid = this.getTargetSeriesUid();
            if (seriesUid) {
                const wl = this.environment?.getImageWindowLevel(seriesUid);
                if (wl) {
                    this.savedWindowLevel = wl.clone();
                    this.savedSeriesUid = seriesUid;
                } else {
                    console.warn(
                        `No window level found for series UID ${seriesUid}`
                    );
                }
            }

            // Request pointer lock — mousedown is the only
            // reliable user-gesture context. If the click turns
            // out to be stationary we release on mouseup.
            this.pointerLockManager.setButtonHeld(true);
            const canvas = view.canvas ?? undefined;
            if (canvas) {
                this.pointerLockManager.requestLock(canvas);
            }

            // Show the neutral sun cursor immediately for early
            // visual feedback, unless the active tool uses
            // stationary right-click during its action (e.g.
            // LineDrawTool undo).
            if (!toolUsesButtonDuringAction(WINDOWING_CONFIG.mouseButton)) {
                this.windowingCursor = new WindowingCursor(
                    this.views ?? null,
                    this.getCursorOptions()
                );
                this.windowingCursor.show(coordinates[0], coordinates[1]);
                this.isPreThresholdCursorShown = true;
            }

            return true;
        }

        // Block all other mouse presses while windowing is active
        if (this._isActive) {
            return true;
        }

        return false;
    }

    /**
     * Windowing handling for mouse move.
     * @param movement Raw movementX/movementY from the
     *  MouseEvent.
     * @param buttons Current button bitmask.
     * @returns true if the event was consumed.
     */
    public onMouseMove(movement: vec2, buttons: number): boolean {
        // If action button was released while we weren't looking
        // (e.g. outside the window), clean up.
        if (
            this.isActionButtonHeld &&
            (buttons & WINDOWING_CONFIG.mouseButtonMask) === 0
        ) {
            this.deactivate();
            return false;
        }

        if (!this.isActionButtonHeld) {
            return false;
        }

        if (!this._isActive) {
            // Accumulate movement for threshold check
            this.accumulatedMovement[0] += movement[0];
            this.accumulatedMovement[1] += movement[1];

            const distance = vec2.length(this.accumulatedMovement);
            if (distance >= WINDOWING_CONFIG.activationThreshold) {
                this.activate();
            } else {
                return true;
            }
        }

        // Active — compute windowing delta
        this.applyWindowingDelta(movement);
        return true;
    }

    /**
     * Windowing handling for mouse release.
     * @returns true if the event was consumed.
     */
    public onMouseRelease(button: number): boolean {
        if (button !== WINDOWING_CONFIG.mouseButton) {
            // Block other button releases while active
            return this._isActive;
        }

        if (this._isActive) {
            this.deactivate();
            return true;
        }

        if (this.isActionButtonHeld) {
            // Stationary click — hide the early cursor, release
            // pointer lock, then let the tool handle it.
            if (this.windowingCursor) {
                this.windowingCursor.hide();
                this.windowingCursor = undefined;
            }
            this.isPreThresholdCursorShown = false;
            this.isActionButtonHeld = false;
            this.pointerLockManager.setButtonHeld(false);
            this.pointerLockManager.releaseLock();
            this.savedWindowLevel = undefined;
            this.savedSeriesUid = undefined;
            return false;
        }

        return false;
    }

    /**
     * Esc key handling — fallback for when pointer lock is NOT
     * active. When pointer lock IS active the browser swallows
     * the Esc keydown to exit the lock; we detect that via the
     * pointer lock manager's onExternalExit callback instead.
     * @returns true if windowing was active and Esc consumed.
     */
    public onEscKey(): boolean {
        if (!this._isActive) {
            return false;
        }

        // If pointer lock is active the browser will consume the
        // Esc keypress itself — this handler won't even run. But
        // guard just in case.
        if (this.pointerLockManager.isLocked) {
            return false;
        }

        this.restoreWindowLevel();
        this.deactivate();
        return true;
    }

    /**
     * Mouse leave handling — cancel pre-threshold state.
     */
    public onMouseLeave(): void {
        if (this.isActionButtonHeld && !this._isActive) {
            if (this.windowingCursor) {
                this.windowingCursor.hide();
                this.windowingCursor = undefined;
            }
            this.isPreThresholdCursorShown = false;
            this.isActionButtonHeld = false;
            this.pointerLockManager.setButtonHeld(false);
            this.pointerLockManager.releaseLock();
            this.savedWindowLevel = undefined;
            this.savedSeriesUid = undefined;
        }
    }

    // ───────── private helpers ─────────

    /**
     * Cursor appearance options matching the viewport HUD.
     * Returns color + label for primary/secondary, or empty
     * options (default white, no label) when no secondary
     * is loaded. When a colored fusion theme is active the
     * cursor uses the theme's color masks; for grayscale
     * fusion it falls back to the app's primary/secondary
     * theme colors.
     */
    private getCursorOptions(): WindowingCursorOptions {
        const hasSecondary =
            this.environment?.secondaryImage?.parent?.seriesUid !== undefined;
        if (!hasSecondary) {
            return {};
        }

        const imageLabel = this.isTargetingSecondary ? 'II' : 'I';

        const useThemeColors =
            this.environment !== undefined && !this.isGrayscaleColorMask();

        if (useThemeColors) {
            const mask = this.isTargetingSecondary
                ? this.environment!.secondaryColorMask
                : this.environment!.colorMask;
            return {
                foregroundColor: vec3ToHex(mask),
                imageLabel,
            };
        }

        const foregroundColor = this.isTargetingSecondary
            ? SECONDARY_COLOR
            : PRIMARY_COLOR;
        return { foregroundColor, imageLabel };
    }

    /**
     * Returns true when both color masks are [1, 1, 1] — the
     * default grayscale state where no colored fusion theme is
     * active.
     */
    private isGrayscaleColorMask(): boolean {
        if (!this.environment) {
            return true;
        }
        const primary = this.environment.colorMask;
        const secondary = this.environment.secondaryColorMask;
        return (
            primary[0] === 1 &&
            primary[1] === 1 &&
            primary[2] === 1 &&
            secondary[0] === 1 &&
            secondary[1] === 1 &&
            secondary[2] === 1
        );
    }

    /**
     * Returns the series UID of the image targeted by global
     * windowing. Targets the secondary image when Alt was held
     * at drag start; otherwise targets the primary image.
     */
    private getTargetSeriesUid(): string | undefined {
        if (this.isTargetingSecondary) {
            return this.environment?.secondaryImage?.parent?.seriesUid;
        }
        return this.environment?.image?.parent?.seriesUid;
    }

    /** Transition from pre-threshold to active windowing. */
    private activate(): void {
        this._isActive = true;
        this.pointerLockManager.setActive(true);
        vec2.zero(this.totalDelta);

        // Show the cursor overlay at the mousedown position
        // (skip if already shown during pre-threshold phase)
        if (!this.isPreThresholdCursorShown) {
            this.windowingCursor = new WindowingCursor(
                this.views ?? null,
                this.getCursorOptions()
            );
            this.windowingCursor.show(
                this.startCoordinates[0],
                this.startCoordinates[1]
            );
        }
        this.isPreThresholdCursorShown = false;
    }

    /** End windowing and clean up all state. */
    private deactivate(): void {
        // Clear flags first so that the pointerlockchange handler
        // (which fires asynchronously after releaseLock) sees
        // the coordinator as inactive and does not re-enter.
        this._isActive = false;
        this.isActionButtonHeld = false;

        this.pointerLockManager.setActive(false);
        this.pointerLockManager.setButtonHeld(false);
        this.pointerLockManager.releaseLock();

        if (this.windowingCursor) {
            this.windowingCursor.hide();
            this.windowingCursor = undefined;
        }

        this.isPreThresholdCursorShown = false;
        this.isTargetingSecondary = false;
        this.savedWindowLevel = undefined;
        this.savedSeriesUid = undefined;
        vec2.zero(this.totalDelta);
    }

    /** Restore the saved WW/WC (Esc / tab-switch cancel). */
    private restoreWindowLevel(): void {
        if (this.savedWindowLevel && this.savedSeriesUid) {
            domainEventBus.publishSync(
                new WindowLevelSetEvent(
                    this.savedWindowLevel,
                    this.savedSeriesUid,
                    'user'
                )
            );
        }
    }

    /** Compute and publish windowing delta from mouse movement. */
    private applyWindowingDelta(movement: vec2): void {
        const seriesUid = this.getTargetSeriesUid();
        if (!seriesUid) {
            return;
        }

        const delta = vec2.create();
        const widthMovement =
            WINDOWING_CONFIG.widthAxis === 'x' ? movement[0] : movement[1];
        const centerMovement =
            WINDOWING_CONFIG.centerAxis === 'y' ? movement[1] : movement[0];

        if (WINDOWING_CONFIG.sensitivityMode === 'proportional') {
            delta[0] = widthMovement * WINDOWING_CONFIG.sensitivity;
            delta[1] = centerMovement * WINDOWING_CONFIG.sensitivity;
        } else {
            if (widthMovement > 0) {
                delta[0] = WINDOWING_CONFIG.stepSize;
            } else if (widthMovement < 0) {
                delta[0] = -WINDOWING_CONFIG.stepSize;
            }
            if (centerMovement > 0) {
                delta[1] = WINDOWING_CONFIG.stepSize;
            } else if (centerMovement < 0) {
                delta[1] = -WINDOWING_CONFIG.stepSize;
            }
        }

        if (delta[0] !== 0 || delta[1] !== 0) {
            this.totalDelta[0] += delta[0];
            this.totalDelta[1] += delta[1];
            this.windowingCursor?.updateDirection(this.totalDelta);

            domainEventBus.publishSync(
                new WindowLevelDeltaEvent(delta, seriesUid, 'user')
            );
        }
    }
}
